import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, Ban, CheckCheck, FileText, Mail } from "lucide-react";

const fmtAmount = (a) => (a || a === 0) ? Number(a).toLocaleString("de-DE", { style: "currency", currency: "EUR" }) : "—";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("de-DE") : "—";
const SOURCE_LABELS = { email_inbox: "E-Mail-Eingang", manual: "Manuell/Upload" };

const Row = ({ label, a, b }) => {
  const same = String(a ?? "") !== "" && String(a ?? "") === String(b ?? "");
  return (
    <div className="grid grid-cols-[90px_1fr_1fr] gap-2 py-1 border-b border-red-100 last:border-0 text-xs">
      <span className="text-red-400">{label}</span>
      <span className="text-slate-700 truncate">{a || "—"}</span>
      <span className={`truncate ${same ? "text-red-700 font-semibold" : "text-slate-700"}`}>{b || "—"}{same && " ⚠"}</span>
    </div>
  );
};

// Wiederverwendbare Vergleichskarte (Drawer-Panel + Upload-Warn-Dialog)
export const DuplicateCompareCard = ({ current, other, labelCurrent = "Dieser Beleg", testId }) => (
  <div className="rounded border border-red-200 bg-white px-2.5 py-2" data-testid={testId || `duplicate-compare-${other.id}`}>
    <div className="grid grid-cols-[90px_1fr_1fr] gap-2 pb-1 text-[11px] font-semibold text-red-600 border-b border-red-200">
      <span></span>
      <span>{labelCurrent}</span>
      <span className="flex items-center gap-1">
        {other.source === "email_inbox" ? <Mail className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
        Vorhandener Beleg{other.deactivated_at ? " (deaktiviert)" : ""}
      </span>
    </div>
    <Row label="Beschreibung" a={current.description} b={other.description} />
    <Row label="Lieferant" a={current.vendor} b={other.vendor} />
    <Row label="Betrag" a={fmtAmount(current.amount)} b={fmtAmount(other.amount)} />
    <Row label="Datum" a={fmtDate(current.date)} b={fmtDate(other.date)} />
    <Row label="RE-Nummer" a={current.invoice_number} b={other.invoice_number} />
    <Row label="Herkunft" a={SOURCE_LABELS[current.source] || current.source} b={SOURCE_LABELS[other.source] || other.source} />
  </div>
);

export const DuplicateReviewPanel = ({ expense, onResolved, onDeactivated }) => {
  const [dups, setDups] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    setResolved(false);
    setDups(null);
    if (expense?.id && Array.isArray(expense.duplicate_of) && expense.duplicate_of.length > 0) {
      api.get(`/expenses/${expense.id}/duplicates`)
        .then((res) => setDups(res.data || []))
        .catch(() => setDups([]));
    }
  }, [expense?.id]);

  if (!expense || !Array.isArray(expense.duplicate_of) || expense.duplicate_of.length === 0) return null;
  if (resolved || expense.duplicate_resolved || expense.deactivated_at) return null;

  const keepBoth = async () => {
    setBusy(true);
    try {
      await api.post(`/expenses/${expense.id}/duplicate-resolve`);
      setResolved(true);
      toast.success("Markierung entfernt — beide Belege bleiben erhalten");
      onResolved?.();
    } catch {
      toast.error("Aktion fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    setBusy(true);
    try {
      await api.post(`/expenses/${expense.id}/deactivate`, { reason: "Duplikat" });
      toast.success("Beleg als Duplikat deaktiviert (reaktivierbar)");
      onDeactivated?.();
    } catch {
      toast.error("Deaktivieren fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  // Umgekehrte Richtung: der VORHANDENE Beleg ist das Duplikat, dieser hier bleibt
  const deactivateOther = async (otherId) => {
    setBusy(true);
    try {
      await api.post(`/expenses/${otherId}/deactivate`, { reason: "Duplikat" });
      const remaining = dups.filter((d) => d.id !== otherId && !d.deactivated_at);
      setDups((prev) => prev.map((d) => d.id === otherId ? { ...d, deactivated_at: new Date().toISOString() } : d));
      if (remaining.length === 0) {
        await api.post(`/expenses/${expense.id}/duplicate-resolve`);
        setResolved(true);
        toast.success("Vorhandener Beleg deaktiviert — dieser Beleg bleibt erhalten");
      } else {
        toast.success("Beleg deaktiviert (reaktivierbar)");
      }
      onResolved?.();
    } catch {
      toast.error("Deaktivieren fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded border border-red-300 bg-red-50 px-3 py-2.5 mt-2 space-y-2" data-testid="duplicate-review-panel">
      <div className="flex items-start gap-2 text-xs text-red-700">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <span><strong>Mögliches Duplikat:</strong> Ein sehr ähnlicher Beleg existiert bereits. Vergleiche die Belege und entscheide, wie du vorgehen möchtest.</span>
      </div>

      {dups === null ? (
        <div className="flex items-center gap-2 text-xs text-red-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Vergleich wird geladen…</div>
      ) : dups.length === 0 ? (
        <p className="text-xs text-red-400">Der verlinkte Beleg wurde inzwischen entfernt.</p>
      ) : (
        dups.map((d) => (
          <div key={d.id} className="space-y-1">
            <DuplicateCompareCard current={expense} other={d} />
            {!d.deactivated_at && (
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => deactivateOther(d.id)}
                  className="text-[11px] font-medium text-red-600 hover:text-red-800 underline underline-offset-2 disabled:opacity-50"
                  data-testid={`duplicate-deactivate-other-${d.id}`}
                  title="Der vorhandene Beleg wird mit Grund 'Duplikat' deaktiviert (reaktivierbar) — dieser Beleg bleibt erhalten"
                >
                  Vorhandenen Beleg deaktivieren (diesen behalten)
                </button>
              </div>
            )}
          </div>
        ))
      )}

      <div className="flex flex-wrap gap-2 pt-0.5">
        <Button size="sm" type="button" variant="outline" disabled={busy}
          className="h-7 px-3 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="duplicate-keep-both-btn" onClick={keepBoth}>
          <CheckCheck className="w-3.5 h-3.5 mr-1" /> Beide behalten
        </Button>
        <Button size="sm" type="button" variant="outline" disabled={busy}
          className="h-7 px-3 text-xs border-red-300 text-red-700 hover:bg-red-100"
          data-testid="duplicate-deactivate-btn" onClick={deactivate}
          title="GoBD-konform: Beleg wird mit Grund 'Duplikat' deaktiviert und ist jederzeit reaktivierbar">
          <Ban className="w-3.5 h-3.5 mr-1" /> Diesen neuen Beleg deaktivieren (Duplikat)
        </Button>
      </div>
    </div>
  );
};

export default DuplicateReviewPanel;
